import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface TimeSlot {
  id: string;
  service_id: string;
  provider_id: string;
  room_id?: string;
  start_time: string;
  end_time: string;
  is_available: boolean;
  max_bookings: number;
  current_bookings: number;
  womens_only: boolean;
  created_at: string;
  updated_at: string;
  service?: {
    id: string;
    name: string;
    duration_minutes: number;
  };
  room?: {
    id: string;
    name: string;
  };
}

export const useTimeSlots = (providerId?: string, serviceId?: string, startDate?: string, endDate?: string) => {
  return useQuery({
    queryKey: ['time-slots', providerId, serviceId, startDate, endDate],
    queryFn: async () => {
      let query = supabase
        .from('time_slots')
        .select(`
          *,
          service:services(id, name, duration_minutes),
          room:rooms(id, name)
        `);
      
      if (providerId) {
        query = query.eq('provider_id', providerId);
      }
      
      if (serviceId) {
        query = query.eq('service_id', serviceId);
      }
      
      // Add date range filtering at the database level
      if (startDate && endDate) {
        query = query.gte('start_time', startDate).lte('start_time', endDate);
      }
      
      const { data, error } = await query
        .order('start_time', { ascending: true });

      if (error) {
        console.error('Error fetching time slots:', error);
        throw error;
      }

      return data as TimeSlot[];
    },
    enabled: !!providerId,
  });
};

export const useCreateTimeSlot = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (timeSlotData: Omit<TimeSlot, 'id' | 'created_at' | 'updated_at' | 'service' | 'room'>) => {
      const { data, error } = await supabase
        .from('time_slots')
        .insert(timeSlotData)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-slots'] });
      toast({
        title: "Time slot created",
        description: "The time slot has been successfully created.",
      });
    },
    onError: (error: any) => {
      // Don't show toast for duplicate errors (23505) - let callers handle it
      if (error?.code === '23505') {
        console.log('Duplicate time slot skipped:', error.message);
        return;
      }
      console.error('Error creating time slot:', error);
      toast({
        title: "Error",
        description: "Failed to create time slot. Please try again.",
        variant: "destructive",
      });
    },
  });
};

export const useUpdateTimeSlot = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<TimeSlot> & { id: string }) => {
      const { data, error } = await supabase
        .from('time_slots')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-slots'] });
      toast({
        title: "Time slot updated",
        description: "The time slot has been successfully updated.",
      });
    },
    onError: (error) => {
      console.error('Error updating time slot:', error);
      toast({
        title: "Error",
        description: "Failed to update time slot. Please try again.",
        variant: "destructive",
      });
    },
  });
};

export const useDeleteTimeSlot = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('time_slots')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-slots'] });
      toast({
        title: "Time slot deleted",
        description: "The time slot has been successfully deleted.",
      });
    },
    onError: (error) => {
      console.error('Error deleting time slot:', error);
      toast({
        title: "Error",
        description: "Failed to delete time slot. Please try again.",
        variant: "destructive",
      });
    },
  });
};

export const useBulkDeleteTimeSlots = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ids: string[]) => {
      const { error, count } = await supabase
        .from('time_slots')
        .delete()
        .in('id', ids)
        .eq('current_bookings', 0);

      if (error) throw error;
      return count;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-slots'] });
    },
    onError: (error) => {
      console.error('Error bulk deleting time slots:', error);
      throw error;
    },
  });
};
